import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import TriangleDiagram from "../../../../components/diagrams/TriangleDiagram";
import RootFractionInput, { parseRootFractionInput } from "../../../../components/inputs/RootFractionInput";
import { formatLatexPreview } from "../../../../components/inputs/RootFractionInput";
import { generateAngleLabel, generateRightTriangleLegs } from "./rightTriangleData";

// Uproszczenie pierwiastka
function simplifySqrt(n) {
  let outside = 1;
  let inside = n;
  for (let i = 2; i * i <= inside; i++) {
    while (inside % (i * i) === 0) {
      inside /= i * i;
      outside *= i;
    }
  }
  return { outside, inside };
}

// NWD
function gcd(a, b) {
  while (b) [a, b] = [b, a % b];
  return Math.abs(a);
}

// Format cos jako pierwiastek z ułamkiem
function formatCosAsSqrtFraction({ a, b, vertex }) {
  const adjacent = vertex === "B" ? a : b;
  const hypotenuse = Math.sqrt(a * a + b * b);

  if (Number.isInteger(hypotenuse)) {
    const divisor = gcd(adjacent, hypotenuse);
    const num = adjacent / divisor;
    const den = hypotenuse / divisor;
    const latex = den === 1 ? `${num}` : `\\dfrac{${num}}{${den}}`;
    return { latex, value: adjacent / hypotenuse };
  }

  const sqrt = simplifySqrt(a * a + b * b);
  const fullNumerator = adjacent * sqrt.outside;
  const fullDenominator = a * a + b * b;
  const divisor = gcd(fullNumerator, fullDenominator);
  const num = fullNumerator / divisor;
  const den = fullDenominator / divisor;
  const sqrtLatex = `\\sqrt{${sqrt.inside}}`;
  const latex = den === 1
    ? `${num === 1 ? "" : num}${sqrtLatex}`
    : `\\dfrac{${num === 1 ? "" : num}${sqrtLatex}}{${den}}`;

  return {
    latex,
    value: adjacent / hypotenuse,
  };
}

export function generateTask() {
  const { a, b, isPythagorean } = generateRightTriangleLegs();

  const vertex = Math.random() < 0.5 ? "B" : "C";
  const data = {
    a,
    b,
    vertex,
    isPythagorean,
    mirrored: Math.random() < 0.5,
    angleLabel: generateAngleLabel(),
  };
  const { latex } = formatCosAsSqrtFraction(data);
  return {
    type: "FindCosFromTriangleTask",
    data,
    expectedAnswer: latex,
  };
}


export function renderTask({ data }) {
  const { a, b, vertex, mirrored, angleLabel = "\\alpha" } = data;
  const angle = vertex === "B" ? "B" : "C";

  return (
    <>
      <p>
        Na podstawie rysunku poniżej, oblicz <InlineMath math={`\\cos${angleLabel}`} />.
        Kąt <InlineMath math={angleLabel} /> znajduje się w wierzchołku <InlineMath math={vertex} />.
      </p>
      <TriangleDiagram a={a} b={b} angleAt={angle} mirrored={mirrored} alphaLabel={angleLabel} />
    </>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const angleLabel = data?.angleLabel || "\\alpha";
  return (
    <RootFractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label={`\\cos${angleLabel}`}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const { latex: expectedLatex, value: expectedValue } = formatCosAsSqrtFraction(data);
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  const parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);
  if (parsed != null && Math.abs(parsed - expectedValue) < 0.01) return true;

  const normalize = (str) =>
    String(str)
      .replace(/\s/g, "")
      .replace(/\*/g, "")
      .replace(/sqrt\(([^)]+)\)/g, "√$1")
      .replace(/\\sqrt{([^}]+)}/g, "√$1")
      .replace(/\\dfrac{([^}]+)}{([^}]+)}/g, "$1/$2")
      .replace(/\\frac{([^}]+)}{([^}]+)}/g, "$1/$2")
      .replace(/[()]/g, "")
      .replace(/\\/g, "");

  return normalize(raw) === normalize(expectedLatex);
}

export function renderAnswer({ data }) {
  const { latex } = formatCosAsSqrtFraction(data);
  const angleLabel = data?.angleLabel || "\\alpha";
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <InlineMath math={`\\cos${angleLabel} = ${latex}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer, task) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== "string" || raw.trim() === "") return <i>(brak odpowiedzi)</i>;
  const angleLabel = task?.data?.angleLabel || "\\alpha";
  return <InlineMath math={`\\displaystyle ${formatLatexPreview(raw, `\\cos${angleLabel}`)}`} />;
}

export function renderExplanation({ data }) {
  const { a, b, vertex } = data;
  const angleLabel = data?.angleLabel || "\\alpha";
  const adjacent = vertex === "B" ? a : b;
  const hypotenuseSquared = a * a + b * b;
  const hypotenuse = Math.sqrt(hypotenuseSquared);
  const { latex, value } = formatCosAsSqrtFraction(data);
  const simplified = simplifySqrt(hypotenuseSquared);

  const root = `\\sqrt{${simplified.inside}}`;
  const denominatorLatex = Number.isInteger(hypotenuse)
    ? String(hypotenuse)
    : simplified.outside === 1 ? root : `${simplified.outside}${root}`;
  const multiplyBy = root;

  return (
    <div className="space-y-4">
      <div>
        <strong>Krok 1:</strong> Korzystamy ze wzoru na cosinus kąta ostrego w trójkącie prostokątnym:
        <BlockMath math={`\\cos${angleLabel} = \\dfrac{\\text{przyprostokątna~przyległa~do~}${angleLabel}}{\\text{przeciwprostokątna}}`} />
      </div>
      <div>
        <strong>Krok 2:</strong> Wstawiamy dane i upraszczamy pierwiastek:
        <BlockMath math={`\\cos${angleLabel} = \\dfrac{${adjacent}}{${denominatorLatex}}`} />
      </div>
      {Number.isInteger(hypotenuse) ? (
        <div>
          <strong>Krok 3:</strong> Skracamy ułamek, jeśli jest to możliwe:
          <BlockMath math={`\\cos${angleLabel}=${latex}`} />
        </div>
      ) : (
        <div>
          <strong>Krok 3:</strong> Usuwamy niewymierność z mianownika przez rozszerzenie tylko przez pierwiastek:
          <BlockMath math={`\\cos${angleLabel} = \\dfrac{${adjacent}}{${denominatorLatex}} \\cdot \\dfrac{${multiplyBy}}{${multiplyBy}} =`} />
          <BlockMath math={`= ${latex}`} />
        </div>
      )}
      <div>
        <strong>Krok 4:</strong> Wartość przybliżona:
        <BlockMath math={`\\cos${angleLabel} \\approx ${value.toFixed(2)}`} />
      </div>
    </div>
  );
}
